import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import PlanPanel from '../components/master-admin/PlanPanel';
import LeadsTab from '../components/master-admin/LeadsTab';
import { 
  Building2, 
  Users, 
  FileText, 
  Plus, 
  Search,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Trash2,
  RefreshCw,
  LogOut,
  Shield,
  ChevronDown,
  ChevronRight,
  Settings,
  Activity,
  Inbox,
  BarChart3,
  UserPlus,
  Key,
  Eye,
  EyeOff,
  RotateCcw,
  TrendingUp,
  Server,
  Mail,
  AlertTriangle,
  Megaphone,
  UserCog,
  Wrench,
  Database,
  Clock,
  Zap,
  CheckSquare,
  Square,
  Pencil,
  Download,
  Upload,
  Copy
} from 'lucide-react';

const API_URL = `${BACKEND_URL}/api`;

export default function MasterAdminPage() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showBullhornModal, setShowBullhornModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDisabledUsers, setShowDisabledUsers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  
  const [newOrg, setNewOrg] = useState({
    name: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    plan: 'documents',
    start_as_trial: true
  });
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user'
  });
  const [bullhornConfig, setBullhornConfig] = useState({
    client_id: '',
    client_secret: '',
    username: '',
    password: '',
    auth_url: 'https://auth.bullhornstaffing.com/oauth/authorize',
    rest_url: 'https://rest.bullhornstaffing.com/rest-services/'
  });
  const [bullhornDetails, setBullhornDetails] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    type: 'info'
  });
  const [showPasswords, setShowPasswords] = useState({});

  // ── Tenant Clone (Export / Import) ──────────────────────────────────
  // Master Admin tool for mirroring a tenant's templates, packets and
  // (safe) settings from one HyreDocs environment into another (e.g.
  // production → preview). Lives entirely client-side until the user
  // triggers an action.
  const [cloneExportTenantId, setCloneExportTenantId] = useState('');
  const [cloneExporting, setCloneExporting] = useState(false);
  const [cloneImportFile, setCloneImportFile] = useState(null);
  const [cloneImportTargetId, setCloneImportTargetId] = useState('');
  const [cloneImportCollision, setCloneImportCollision] = useState('replace');
  const [cloneImportIncludePackets, setCloneImportIncludePackets] = useState(true);
  const [cloneImportIncludeSettings, setCloneImportIncludeSettings] = useState(true);
  const [cloneImporting, setCloneImporting] = useState(false);
  const [cloneImportResult, setCloneImportResult] = useState(null);
  const [cloneExportPreview, setCloneExportPreview] = useState(null);

  // Detect which HyreDocs environment we're currently rendering on so
  // the Clone screen can be explicit about "you're exporting from
  // PRODUCTION" vs "importing into PREVIEW". Falls back to "Local" for
  // dev. Matches the host once at module-load — env never changes mid-
  // session.
  const cloneEnv = (() => {
    if (typeof window === 'undefined') return { id: 'unknown', label: 'Unknown' };
    const host = window.location.hostname || '';
    if (host.includes('preview.emergentagent.com') || host.includes('preview.hyredocs')) {
      return { id: 'preview', label: 'Preview', tone: 'amber' };
    }
    if (host === 'hyredocs.com' || host.endsWith('.hyredocs.com')) {
      return { id: 'production', label: 'Production', tone: 'red' };
    }
    if (host === 'localhost' || host.startsWith('127.')) {
      return { id: 'local', label: 'Local', tone: 'slate' };
    }
    return { id: 'unknown', label: host, tone: 'slate' };
  })();
  // The "other side" of a typical clone — production exports to preview
  // and vice versa. Used purely for copywriting.
  const cloneOtherEnvLabel =
    cloneEnv.id === 'production' ? 'Preview' :
    cloneEnv.id === 'preview' ? 'Production' :
    'another environment';

  useEffect(() => {
    if (authLoading) return;
    
    if (!user?.is_master_admin) {
      toast.error('Access denied. Master admin only.');
      navigate('/login');
      return;
    }
    loadOverview();
    loadOrganizations();
  }, [user, authLoading]);

  const loadOverview = async () => {
    try {
      const response = await axios.get(`${API_URL}/master/stats/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to load overview:', error);
    }
  };

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await axios.get(`${API_URL}/master/organizations`, { params });
      setOrganizations(response.data.organizations);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrgDetails = async (orgId, includeInactiveUsers = showDisabledUsers) => {
    try {
      const response = await axios.get(`${API_URL}/master/organizations/${orgId}`, {
        params: { include_inactive_users: includeInactiveUsers ? 'true' : 'false' },
      });
      setSelectedOrg(response.data);
    } catch (error) {
      toast.error('Failed to load organization details');
    }
  };

  const handleReactivateUser = async (userId, userEmail) => {
    if (!selectedOrg) return;
    if (!window.confirm(`Reactivate ${userEmail}? They'll be able to sign in again.`)) return;
    try {
      await axios.post(`${API_URL}/master/organizations/${selectedOrg.organization.id}/users/${userId}/reactivate`);
      toast.success(`${userEmail} reactivated`);
      loadOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const loadActivityLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/master/activity-logs`, { params: { limit: 50 } });
      setActivityLogs(response.data.logs);
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const [healthRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/master/system/health`),
        axios.get(`${API_URL}/master/system/stats`)
      ]);
      setSystemHealth({ ...healthRes.data, stats: statsRes.data });
    } catch (error) {
      console.error('Failed to load system health:', error);
    }
  };

  const loadPlatformSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/master/settings`);
      setPlatformSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/master/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!newOrg.name || !newOrg.admin_email || !newOrg.admin_name || !newOrg.admin_password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/master/organizations`, newOrg);
      toast.success(`Organization "${response.data.organization.name}" created successfully!`);
      setShowCreateModal(false);
      setNewOrg({ name: '', admin_email: '', admin_name: '', admin_password: '', plan: 'documents', start_as_trial: true });
      loadOrganizations();
      loadOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteOrganization = async (org) => {
    if (!window.confirm(`Are you sure you want to deactivate "${org.name}"? All users will lose access.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/master/organizations/${org.id}`);
      toast.success(`Organization "${org.name}" has been deactivated`);
      loadOrganizations();
      loadOverview();
      setSelectedOrg(null);
      setExpandedOrg(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    }
  };

  const handleBulkDelete = async (permanent = false) => {
    if (selectedOrgs.length === 0) {
      toast.error('Select organizations to delete');
      return;
    }
    
    const action = permanent ? 'permanently delete' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedOrgs.length} organizations?`)) {
      return;
    }
    
    try {
      await axios.post(`${API_URL}/master/organizations/bulk-delete`, {
        organization_ids: selectedOrgs,
        permanent
      });
      toast.success(`${selectedOrgs.length} organizations ${permanent ? 'deleted' : 'deactivated'}`);
      setSelectedOrgs([]);
      loadOrganizations();
      loadOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organizations');
    }
  };

  const handleCleanupTestOrgs = async (permanent = false) => {
    if (!window.confirm(`This will ${permanent ? 'permanently delete' : 'deactivate'} all organizations starting with "TEST_". Continue?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_URL}/master/organizations/cleanup-test?permanent=${permanent}`);
      toast.success(response.data.message);
      loadOrganizations();
      loadOverview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup test organizations');
    }
  };

  const handleImpersonate = async (userId, email) => {
    if (!window.confirm(`Impersonate ${email}? You'll be logged in as this user.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/master/impersonate/${userId}`);
      const { user: impUser, tenant, login_url } = response.data;
      
      // Cookie is set by the backend response — just store UI state
      localStorage.setItem('tenantSlug', tenant?.slug || 'default');
      localStorage.setItem('impersonating', 'true');
      
      toast.success(`Now impersonating ${impUser.name}`);
      window.location.href = login_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  // Open organization dashboard by impersonating its admin user
  const handleOpenOrgDashboard = async (org) => {
    try {
      let users = org.users;
      if (!users || users.length === 0) {
        const response = await axios.get(`${API_URL}/master/organizations/${org.id}/users`);
        users = response.data.users || [];
      }
      
      const adminUser = users.find(u => u.role === 'admin') || users[0];
      
      if (!adminUser) {
        toast.error('No users found in this organization');
        return;
      }
      
      const response = await axios.post(`${API_URL}/master/impersonate/${adminUser.id}`);
      const { user: impUser, tenant, login_url } = response.data;
      
      // Cookie is set by the backend response
      localStorage.setItem('tenantSlug', tenant?.slug || 'default');
      localStorage.setItem('impersonating', 'true');
      
      toast.success(`Opening ${org.name} dashboard as ${impUser.name}`);
      window.location.href = login_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open organization dashboard');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_URL}/master/settings`, platformSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/master/announcements`, newAnnouncement);
      toast.success('Announcement created');
      setShowAnnouncementModal(false);
      setNewAnnouncement({ title: '', message: '', type: 'info' });
      loadAnnouncements();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API_URL}/master/announcements/${id}`);
      toast.success('Announcement deleted');
      loadAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    
    try {
      await axios.post(
        `${API_URL}/master/organizations/${selectedOrg.organization.id}/users`,
        newUser
      );
      toast.success(`User "${newUser.name}" added successfully`);
      setShowAddUserModal(false);
      setNewUser({ email: '', name: '', password: '', role: 'user' });
      loadOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!selectedOrg) return;
    if (!window.confirm(`Remove user ${email}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/master/organizations/${selectedOrg.organization.id}/users/${userId}`);
      toast.success('User removed');
      loadOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleResetPassword = async (userId, email) => {
    if (!selectedOrg) return;
    try {
      const response = await axios.post(`${API_URL}/master/organizations/${selectedOrg.organization.id}/users/${userId}/reset-password`);
      const { temporary_password } = response.data;
      // Show the temporary password in a copyable way
      toast.success(
        <div className="space-y-1">
          <p className="font-medium">Password reset for {email}</p>
          <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{temporary_password}</p>
          <p className="text-xs text-muted-foreground">Copy this — it won't be shown again</p>
        </div>,
        { duration: 15000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleSetPassword = async (userId, email) => {
    if (!selectedOrg) return;
    const newPassword = window.prompt(`Set new password for ${email} (min 6 characters):`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API_URL}/master/organizations/${selectedOrg.organization.id}/users/${userId}/set-password`, { password: newPassword });
      toast.success(`Password updated for ${email}`);
      loadOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !editingUser) return;
    try {
      await axios.put(`${API_URL}/master/organizations/${selectedOrg.organization.id}/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role
      });
      toast.success('User updated');
      setEditingUser(null);
      loadOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUpdateBullhorn = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    
    try {
      await axios.put(`${API_URL}/master/organizations/${selectedOrg.organization.id}/bullhorn`, bullhornConfig);
      toast.success('Bullhorn configuration saved');
      setShowBullhornModal(false);
      setBullhornConfig({ client_id: '', client_secret: '', username: '', password: '', auth_url: 'https://auth.bullhornstaffing.com/oauth/authorize', rest_url: 'https://rest.bullhornstaffing.com/rest-services/' });
      loadOrgDetails(selectedOrg.organization.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Bullhorn config');
    }
  };

  const handleTestConnection = async (orgId) => {
    setTestingConnection(true);
    try {
      const response = await axios.post(`${API_URL}/master/organizations/${orgId}/bullhorn/test-connection`);
      if (response.data.success) {
        toast.success(response.data.message || 'Connected to Bullhorn!');
        loadOrgDetails(orgId);
        loadOrganizations();
      } else {
        toast.error(`Connection failed: ${response.data.error}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDisconnectBullhorn = async (orgId) => {
    setDisconnecting(true);
    try {
      await axios.post(`${API_URL}/master/organizations/${orgId}/bullhorn/disconnect`);
      toast.success('Bullhorn disconnected');
      loadOrgDetails(orgId);
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const loadBullhornDetails = async (orgId) => {
    try {
      const response = await axios.get(`${API_URL}/master/organizations/${orgId}/bullhorn`);
      setBullhornDetails(response.data);
    } catch (error) {
      setBullhornDetails(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ── Tenant Clone handlers ───────────────────────────────────────────
  const handleExportTenant = async () => {
    if (!cloneExportTenantId) {
      toast.error('Pick a source tenant first');
      return;
    }
    setCloneExporting(true);
    setCloneExportPreview(null);
    try {
      // GET the envelope as JSON, then trigger a browser download.
      const response = await axios.get(
        `${API_URL}/admin/tenant-clone/${cloneExportTenantId}/export`,
        { responseType: 'json' }
      );
      const envelope = response.data;
      const sourceSlug = envelope?.source_tenant?.slug || cloneExportTenantId;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hyredocs-clone-${sourceSlug}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const totalBytes = (envelope.templates || []).reduce(
        (acc, t) => acc + (t.pdf_base64 ? Math.floor((t.pdf_base64.length * 3) / 4) : 0),
        0
      );
      setCloneExportPreview({
        templates: (envelope.templates || []).length,
        packets: (envelope.packets || []).length,
        size_bytes: totalBytes,
        source: envelope?.source_tenant,
      });
      toast.success(
        `Exported ${(envelope.templates || []).length} templates and ${(envelope.packets || []).length} packets`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setCloneExporting(false);
    }
  };

  const handleImportTenant = async () => {
    if (!cloneImportFile) {
      toast.error('Choose an export file to upload');
      return;
    }
    if (!cloneImportTargetId) {
      toast.error('Pick a target tenant');
      return;
    }
    const targetOrg = organizations.find(o => o.id === cloneImportTargetId);
    const confirmMsg =
      `Import into "${targetOrg?.name || cloneImportTargetId}"?\n\n` +
      `Collision: ${cloneImportCollision}\n` +
      `Packets: ${cloneImportIncludePackets ? 'yes' : 'no'}\n` +
      `Settings: ${cloneImportIncludeSettings ? 'yes' : 'no'}`;
    if (!window.confirm(confirmMsg)) return;

    setCloneImporting(true);
    setCloneImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', cloneImportFile);
      fd.append('target_tenant_id', cloneImportTargetId);
      fd.append('name_collision', cloneImportCollision);
      fd.append('include_packets', cloneImportIncludePackets ? 'true' : 'false');
      fd.append('include_settings', cloneImportIncludeSettings ? 'true' : 'false');
      const response = await axios.post(`${API_URL}/admin/tenant-clone/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setCloneImportResult(response.data?.summary || null);
      toast.success('Import complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setCloneImporting(false);
    }
  };

  const toggleSelectOrg = (orgId) => {
    setSelectedOrgs(prev => 
      prev.includes(orgId) 
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrgs.length === organizations.length) {
      setSelectedOrgs([]);
    } else {
      setSelectedOrgs(organizations.map(o => o.id));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      trial: 'bg-amber-100 text-amber-700',
      suspended: 'bg-red-100 text-red-700',
      deleted: 'bg-gray-100 text-gray-500'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {status}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    // Style + label map. Includes legacy values (`trial`, `professional`)
    // so existing tenants don't render as undefined; they map to the
    // nearest new-catalog equivalent. Display names mirror the public
    // pricing page tier names (Starter / Pro / Compliance) — the
    // underlying plan IDs stay `forms` / `documents` / `complete`.
    const meta = {
      forms:        { label: 'Starter',     cls: 'bg-sky-50 text-sky-700 border-sky-200' },
      documents:    { label: 'Pro',         cls: 'bg-blue-50 text-blue-700 border-blue-200' },
      complete:     { label: 'Compliance',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      enterprise:   { label: 'Enterprise',  cls: 'bg-purple-50 text-purple-700 border-purple-200' },
      trial:        { label: 'Trial',       cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      professional: { label: 'Pro',         cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    };
    const m = meta[plan] || { label: plan || '—', cls: 'bg-slate-50 text-slate-600 border-slate-200' };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${m.cls}`}>
        {m.label}
      </span>
    );
  };

  const toggleExpand = (orgId) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      setSelectedOrg(null);
      setBullhornDetails(null);
    } else {
      setExpandedOrg(orgId);
      loadOrgDetails(orgId);
      loadBullhornDetails(orgId);
    }
  };

  const getHealthIcon = (status) => {
    if (status === 'healthy' || status === 'configured') return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'organizations', label: 'Organizations', icon: Building2 },
    { id: 'leads', label: 'Leads', icon: Inbox },
    { id: 'clone', label: 'Clone', icon: Copy },
    { id: 'health', label: 'System', icon: Server },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'activity', label: 'Activity', icon: Activity }
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-md">
              <HyreDocsIcon className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl">HyreDocs</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Shield className="w-3 h-3" />
                Master Admin Panel
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-6 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'activity') loadActivityLogs();
                  if (tab.id === 'health') loadSystemHealth();
                  if (tab.id === 'settings') { loadPlatformSettings(); loadAnnouncements(); }
                }}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="neumorphic rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{overview.organizations.total}</p>
                    <p className="text-sm text-muted-foreground">Organizations</p>
                  </div>
                </div>
                <p className="text-xs text-emerald-600 mt-2">+{overview.organizations.new_last_30_days} this month</p>
              </div>
              
              <div className="neumorphic rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{overview.users.total}</p>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                  </div>
                </div>
                <p className="text-xs text-emerald-600 mt-2">+{overview.users.new_last_30_days} this month</p>
              </div>
              
              <div className="neumorphic rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{overview.documents.total}</p>
                    <p className="text-sm text-muted-foreground">Documents</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{overview.documents.completion_rate}% completion rate</p>
              </div>
              
              <div className="neumorphic rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{overview.organizations.with_bullhorn}</p>
                    <p className="text-sm text-muted-foreground">Bullhorn Connected</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="neumorphic rounded-xl p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">Top Organizations by Documents</h3>
              <div className="space-y-3">
                {overview.top_organizations?.length > 0 ? overview.top_organizations.map((org, idx) => (
                  <div key={org._id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium">{org.name || org._id}</p>
                        {org.slug && <p className="text-xs text-muted-foreground">/{org.slug}</p>}
                      </div>
                    </div>
                    <span className="text-sm font-semibold">{org.count} docs</span>
                  </div>
                )) : (
                  <p className="text-muted-foreground text-center py-4">No data yet</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Organizations Tab */}
        {activeTab === 'organizations' && (
          <div className="neumorphic rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-semibold">Organizations</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleCleanupTestOrgs(true)} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Cleanup TEST_*
                </Button>
                <Button onClick={() => setShowCreateModal(true)} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Organization
                </Button>
              </div>
            </div>

            {/* Search & Bulk Actions */}
            <div className="flex gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search organizations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loadOrganizations()}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" onClick={loadOrganizations}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {selectedOrgs.length > 0 && (
              <div className="flex items-center gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
                <span className="text-sm font-medium">{selectedOrgs.length} selected</span>
                <Button size="sm" variant="outline" onClick={() => handleBulkDelete(false)}>
                  Deactivate Selected
                </Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleBulkDelete(true)}>
                  Delete Permanently
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedOrgs([])}>
                  Clear
                </Button>
              </div>
            )}

            {/* Organizations List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              </div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No organizations found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Select All */}
                <div className="flex items-center gap-3 px-4 py-2 text-sm text-muted-foreground">
                  <button onClick={toggleSelectAll} className="hover:text-foreground">
                    {selectedOrgs.length === organizations.length ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                  <span>Select all</span>
                </div>

                {organizations.map((org) => (
                  <div key={org.id} className="border border-border rounded-lg overflow-hidden">
                    <div className="flex items-center gap-3 p-4 hover:bg-muted/30">
                      <button onClick={() => toggleSelectOrg(org.id)} className="text-muted-foreground hover:text-foreground">
                        {selectedOrgs.includes(org.id) ? (
                          <CheckSquare className="w-4 h-4 text-primary" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </button>
                      
                      <button 
                        className="flex-1 flex items-center justify-between cursor-pointer"
                        onClick={() => toggleExpand(org.id)}
                      >
                        <div className="flex items-center gap-4">
                          {expandedOrg === org.id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                          <div className="text-left">
                            <p className="font-medium">{org.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">/{org.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {getPlanBadge(org.plan || 'professional')}
                          {getStatusBadge(org.status)}
                          {org.bullhorn_config?.is_connected ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700" data-testid={`bullhorn-status-${org.id}`}>
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              API
                            </span>
                          ) : org.bullhorn_config?.has_credentials ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700" data-testid={`bullhorn-status-${org.id}`}>
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                              API
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700" data-testid={`bullhorn-status-${org.id}`}>
                              <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                              No API
                            </span>
                          )}
                          <span className="text-sm text-muted-foreground">{org.user_count || 0} users</span>
                        </div>
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {expandedOrg === org.id && selectedOrg && (
                      <div className="border-t border-border p-4 bg-muted/20">
                        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
                          {/* Stats */}
                          <div className="space-y-4">
                            <h4 className="font-semibold text-sm">Statistics</h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Documents</span>
                                <span className="font-medium">{selectedOrg.stats.document_count}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Completed</span>
                                <span className="font-medium">{selectedOrg.stats.completed_documents}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Templates</span>
                                <span className="font-medium">{selectedOrg.stats.template_count || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Created</span>
                                <span className="font-medium">
                                  {new Date(selectedOrg.organization.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Users */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-sm">Users</h4>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-disabled-users-toggle">
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 rounded border-input"
                                    checked={showDisabledUsers}
                                    onChange={(ev) => {
                                      const next = ev.target.checked;
                                      setShowDisabledUsers(next);
                                      if (selectedOrg) loadOrgDetails(selectedOrg.organization.id, next);
                                    }}
                                  />
                                  Show disabled
                                </label>
                                <Button size="sm" variant="outline" onClick={() => setShowAddUserModal(true)}>
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  Add
                                </Button>
                              </div>
                            </div>
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {selectedOrg.users.map(u => (
                                <div
                                  key={u.id}
                                  className={`flex items-center justify-between text-sm p-2 rounded ${u.is_active === false ? 'bg-slate-50 opacity-70' : 'bg-white'}`}
                                  data-testid={`user-row-${u.id}`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium">{u.name}</p>
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                      <span className="font-mono break-all" title="Login email">{u.email}</span>
                                      {u.plain_password && (
                                        <>
                                          <span className="text-slate-300">|</span>
                                          <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-600" title="Password">{u.plain_password}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1 ml-2">
                                    {u.is_active === false && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">disabled</span>
                                    )}
                                    <span className={`text-xs px-2 py-0.5 rounded ${u.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-muted'}`}>
                                      {u.role}
                                    </span>
                                    {u.is_active === false ? (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-1.5 text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
                                        onClick={() => handleReactivateUser(u.id, u.email)}
                                        data-testid={`reactivate-user-${u.id}`}
                                        title="Reactivate user"
                                      >
                                        <RotateCcw className="w-3 h-3 mr-0.5" />
                                        Re-enable
                                      </Button>
                                    ) : (
                                      <>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0"
                                          onClick={() => setEditingUser({ ...u })}
                                          title="Edit user"
                                        >
                                          <Pencil className="w-3 h-3" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0"
                                          onClick={() => handleImpersonate(u.id, u.email)}
                                          title="Impersonate user"
                                        >
                                          <UserCog className="w-3 h-3" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 px-1.5 text-xs"
                                          onClick={() => handleSetPassword(u.id, u.email)}
                                          title="Set password"
                                          data-testid={`set-password-${u.id}`}
                                        >
                                          <Key className="w-3 h-3 mr-0.5" />
                                          Set pw
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0 text-red-500"
                                          onClick={() => handleDeleteUser(u.id, u.email)}
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Bullhorn Config */}
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-sm">Bullhorn API Connection</h4>
                              <Button size="sm" variant="outline" onClick={() => setShowBullhornModal(true)} data-testid="configure-bullhorn-btn">
                                <Key className="w-3 h-3 mr-1" />
                                Configure
                              </Button>
                            </div>
                            <div className="p-3 bg-white rounded-lg border space-y-3">
                              {/* Connection Status */}
                              <div className="flex items-center gap-2">
                                {selectedOrg.organization.bullhorn_config?.is_connected ? (
                                  <>
                                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                                    <span className="text-sm font-medium text-emerald-700">Connected</span>
                                  </>
                                ) : selectedOrg.organization.bullhorn_config?.has_credentials ? (
                                  <>
                                    <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                                    <span className="text-sm font-medium text-amber-700">Credentials Saved - Not Connected</span>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                                    <span className="text-sm font-medium text-red-700">Not Connected - No Credentials</span>
                                  </>
                                )}
                              </div>
                              
                              {/* Bullhorn Details */}
                              {bullhornDetails && (
                                <div className="space-y-1 text-xs text-muted-foreground border-t pt-2">
                                  {bullhornDetails.region && (
                                    <div className="flex justify-between">
                                      <span>Region</span>
                                      <span className="font-medium text-foreground">{bullhornDetails.region}</span>
                                    </div>
                                  )}
                                  {bullhornDetails.username && (
                                    <div className="flex justify-between">
                                      <span>API User</span>
                                      <span className="font-mono text-foreground">{bullhornDetails.username}</span>
                                    </div>
                                  )}
                                  {bullhornDetails.last_connected_at && (
                                    <div className="flex justify-between">
                                      <span>Last Connected</span>
                                      <span className="text-foreground">{new Date(bullhornDetails.last_connected_at).toLocaleString()}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Action Buttons */}
                              <div className="flex gap-2 pt-1">
                                {selectedOrg.organization.bullhorn_config?.has_credentials && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant={selectedOrg.organization.bullhorn_config?.is_connected ? "outline" : "default"}
                                      onClick={() => handleTestConnection(org.id)}
                                      disabled={testingConnection}
                                      className="flex-1"
                                      data-testid="test-connection-btn"
                                    >
                                      {testingConnection ? (
                                        <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Testing...</>
                                      ) : (
                                        <><Zap className="w-3 h-3 mr-1" /> {selectedOrg.organization.bullhorn_config?.is_connected ? 'Reconnect' : 'Connect'}</>
                                      )}
                                    </Button>
                                    {selectedOrg.organization.bullhorn_config?.is_connected && (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-red-600 hover:bg-red-50"
                                        onClick={() => handleDisconnectBullhorn(org.id)}
                                        disabled={disconnecting}
                                        data-testid="disconnect-bullhorn-btn"
                                      >
                                        {disconnecting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Subscription / Plan management */}
                          <div className="space-y-4">
                            <PlanPanel
                              tenantId={selectedOrg.organization.id}
                              tenantName={selectedOrg.organization.name}
                            />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenOrgDashboard(org)}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Open Dashboard
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteOrganization(org)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Deactivate
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Leads Tab — pricing-page contact-form submissions */}
        {activeTab === 'leads' && <LeadsTab />}

        {/* Clone Tab — Tenant export / import (prod ⇄ preview) */}
        {activeTab === 'clone' && (
          <div className="space-y-6">
            <div className="neumorphic rounded-xl p-6">
              <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Copy className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-heading text-xl font-semibold">Tenant Clone — Export / Import</h2>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                        cloneEnv.tone === 'red'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : cloneEnv.tone === 'amber'
                          ? 'bg-amber-50 text-amber-800 border-amber-200'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}
                      data-testid="clone-env-badge"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        cloneEnv.tone === 'red' ? 'bg-red-500' :
                        cloneEnv.tone === 'amber' ? 'bg-amber-500' : 'bg-slate-400'
                      }`} />
                      You are on: {cloneEnv.label}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Mirror a tenant's templates, packets and (safe) settings between HyreDocs environments.
                    Bullhorn credentials are intentionally stripped from the export.
                  </p>
                </div>
              </div>

              {/* Workflow strip */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                  <div className="text-xs text-emerald-900">
                    <p className="font-semibold">Run Export on Production</p>
                    <p className="text-emerald-800/80 mt-0.5">
                      Log in to <span className="font-mono">hyredocs.com</span> → Master Admin → Clone tab → pick a tenant → download the JSON file.
                    </p>
                  </div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                  <div className="text-xs text-blue-900">
                    <p className="font-semibold">Run Import on Preview</p>
                    <p className="text-blue-800/80 mt-0.5">
                      Log in to the <span className="font-mono">preview</span> environment → Master Admin → Clone tab → upload the JSON → pick the target tenant.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* EXPORT */}
                <div className="rounded-xl border border-border p-5 bg-white" data-testid="tenant-clone-export-card">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Download className="w-4 h-4 text-emerald-600" />
                      <h3 className="font-heading font-semibold">Export from {cloneEnv.label}</h3>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Step 1</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Reads templates, packets and (safe) tenant settings from the <span className="font-medium">{cloneEnv.label}</span> database you're logged in to right now.
                  </p>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Source tenant ({cloneEnv.label})</Label>
                      <select
                        value={cloneExportTenantId}
                        onChange={(e) => setCloneExportTenantId(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="clone-export-source-select"
                      >
                        <option value="">— Select a tenant —</option>
                        {organizations.map(o => (
                          <option key={o.id} value={o.id}>
                            {o.name} (/{o.slug})
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button
                      onClick={handleExportTenant}
                      disabled={cloneExporting || !cloneExportTenantId}
                      className="btn-primary w-full"
                      data-testid="clone-export-btn"
                    >
                      {cloneExporting ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Exporting…</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" /> Export &amp; Download JSON</>
                      )}
                    </Button>
                    {cloneExportPreview && (
                      <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs space-y-1" data-testid="clone-export-preview">
                        <p className="font-medium text-emerald-800">
                          Exported {cloneExportPreview.source?.name || 'tenant'} from <span className="font-semibold">{cloneEnv.label}</span>
                        </p>
                        <p className="text-emerald-700/80 text-[11px]">
                          These counts reflect what's in {cloneEnv.label} right now — not what's in {cloneOtherEnvLabel}.
                        </p>
                        <div className="flex justify-between text-emerald-700 pt-1">
                          <span>Templates</span><span>{cloneExportPreview.templates}</span>
                        </div>
                        <div className="flex justify-between text-emerald-700">
                          <span>Packets</span><span>{cloneExportPreview.packets}</span>
                        </div>
                        <div className="flex justify-between text-emerald-700">
                          <span>PDF payload</span>
                          <span>{(cloneExportPreview.size_bytes / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* IMPORT */}
                <div className="rounded-xl border border-border p-5 bg-white" data-testid="tenant-clone-import-card">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4 text-primary" />
                      <h3 className="font-heading font-semibold">Import into {cloneEnv.label}</h3>
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Step 2</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">
                    Writes the uploaded export into the <span className="font-medium">{cloneEnv.label}</span> database. Use a file you downloaded from {cloneOtherEnvLabel}.
                  </p>
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Export file (.json from {cloneOtherEnvLabel})</Label>
                      <Input
                        type="file"
                        accept="application/json,.json"
                        onChange={(e) => setCloneImportFile(e.target.files?.[0] || null)}
                        data-testid="clone-import-file"
                      />
                      {cloneImportFile && (
                        <p className="text-xs text-muted-foreground">
                          {cloneImportFile.name} — {(cloneImportFile.size / 1024).toFixed(1)} KB
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Target tenant ({cloneEnv.label})</Label>
                      <select
                        value={cloneImportTargetId}
                        onChange={(e) => setCloneImportTargetId(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="clone-import-target-select"
                      >
                        <option value="">— Select a tenant —</option>
                        {organizations.map(o => (
                          <option key={o.id} value={o.id}>
                            {o.name} (/{o.slug})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">If a name already exists</Label>
                      <div className="flex gap-2">
                        {[
                          { v: 'skip', label: 'Skip' },
                          { v: 'rename', label: 'Rename' },
                          { v: 'replace', label: 'Replace' },
                        ].map(opt => (
                          <Button
                            key={opt.v}
                            type="button"
                            size="sm"
                            variant={cloneImportCollision === opt.v ? 'default' : 'outline'}
                            onClick={() => setCloneImportCollision(opt.v)}
                            data-testid={`clone-import-collision-${opt.v}`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {cloneImportCollision === 'skip' && 'Keep the destination copy untouched. Useful as a dry-run check.'}
                        {cloneImportCollision === 'rename' && 'Import as "<name> (imported)" alongside the existing copy.'}
                        {cloneImportCollision === 'replace' && `Overwrite existing templates in the target with the imported versions. This is the most common option when syncing environments.`}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cloneImportIncludePackets}
                          onChange={(e) => setCloneImportIncludePackets(e.target.checked)}
                          data-testid="clone-import-include-packets"
                        />
                        Include packets
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cloneImportIncludeSettings}
                          onChange={(e) => setCloneImportIncludeSettings(e.target.checked)}
                          data-testid="clone-import-include-settings"
                        />
                        Include tenant settings
                      </label>
                    </div>

                    <Button
                      onClick={handleImportTenant}
                      disabled={cloneImporting || !cloneImportFile || !cloneImportTargetId}
                      className="btn-primary w-full"
                      data-testid="clone-import-btn"
                    >
                      {cloneImporting ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Run Import into {cloneEnv.label}</>
                      )}
                    </Button>

                    {cloneImportResult && (
                      <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs space-y-2" data-testid="clone-import-result">
                        <p className="font-medium text-blue-800">Import summary</p>
                        {['templates', 'packets'].map(kind => (
                          <div key={kind} className="space-y-0.5">
                            <p className="text-blue-900 font-medium capitalize">{kind}</p>
                            <div className="grid grid-cols-4 gap-1 text-blue-700">
                              <span>+{cloneImportResult[kind]?.added || 0} added</span>
                              <span>~{cloneImportResult[kind]?.replaced || 0} replaced</span>
                              <span>↻{cloneImportResult[kind]?.renamed || 0} renamed</span>
                              <span>·{cloneImportResult[kind]?.skipped || 0} skipped</span>
                            </div>
                            {(cloneImportResult[kind]?.errors || []).length > 0 && (
                              <p className="text-red-600">
                                {cloneImportResult[kind].errors.length} error(s) — see backend logs
                              </p>
                            )}
                          </div>
                        ))}
                        {cloneImportResult.settings?.applied && (
                          <p className="text-blue-700">
                            Settings applied: {(cloneImportResult.settings.keys || []).join(', ') || '(none)'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <p className="font-medium mb-1">Heads up</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Counts shown here reflect <span className="font-semibold">{cloneEnv.label}</span>'s database — not {cloneOtherEnvLabel}'s.</li>
                  <li>Bullhorn credentials are <span className="font-medium">not</span> exported — each environment keeps its own OAuth app.</li>
                  <li>Template IDs are preserved on import when possible, so existing Bullhorn audit trails keep lining up.</li>
                  <li>"Replace" deletes {cloneEnv.label}'s template (and its PDF) before re-inserting from the export. Use with care.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* System Health Tab */}
        {activeTab === 'health' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-semibold">System Health</h2>
              <Button variant="outline" size="sm" onClick={loadSystemHealth}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>

            {systemHealth ? (
              <>
                {/* Health Checks */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(systemHealth.checks).map(([key, check]) => (
                    <div key={key} className="neumorphic rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        {getHealthIcon(check.status)}
                        <div>
                          <p className="font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">{check.message || check.status}</p>
                        </div>
                      </div>
                      {check.count !== undefined && (
                        <p className="text-xs text-muted-foreground mt-2">Count: {check.count}</p>
                      )}
                      {check.count_24h !== undefined && (
                        <p className="text-xs text-muted-foreground mt-2">Last 24h: {check.count_24h}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* System Stats */}
                {systemHealth.stats && (
                  <div className="neumorphic rounded-xl p-6">
                    <h3 className="font-heading font-semibold mb-4">Usage Statistics</h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Documents</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total</span>
                            <span>{systemHealth.stats.documents.total}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Last 24h</span>
                            <span>{systemHealth.stats.documents.last_24h}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Last 7d</span>
                            <span>{systemHealth.stats.documents.last_7d}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Completion Rate</span>
                            <span>{systemHealth.stats.documents.completion_rate}%</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Users</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total</span>
                            <span>{systemHealth.stats.users.total}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Active (24h)</span>
                            <span>{systemHealth.stats.users.active_24h}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Emails</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total Sent</span>
                            <span>{systemHealth.stats.emails.total_sent}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Last 24h</span>
                            <span>{systemHealth.stats.emails.last_24h}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Platform Settings */}
            <div className="neumorphic rounded-xl p-6">
              <h3 className="font-heading font-semibold text-lg mb-6">Platform Settings</h3>
              
              {platformSettings ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Support Email</Label>
                      <Input
                        value={platformSettings.support_email || ''}
                        onChange={(e) => setPlatformSettings({...platformSettings, support_email: e.target.value})}
                        placeholder="support@company.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Default Sender Name</Label>
                      <Input
                        value={platformSettings.default_sender_name || ''}
                        onChange={(e) => setPlatformSettings({...platformSettings, default_sender_name: e.target.value})}
                        placeholder="HyreDocs"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Trial Period (days)</Label>
                      <Input
                        type="number"
                        value={platformSettings.trial_days || 14}
                        onChange={(e) => setPlatformSettings({...platformSettings, trial_days: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Max Users per Org</Label>
                      <Input
                        type="number"
                        value={platformSettings.max_users_per_org || 50}
                        onChange={(e) => setPlatformSettings({...platformSettings, max_users_per_org: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Documents per Org</Label>
                      <Input
                        type="number"
                        value={platformSettings.max_documents_per_org || 1000}
                        onChange={(e) => setPlatformSettings({...platformSettings, max_documents_per_org: parseInt(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Templates per Org</Label>
                      <Input
                        type="number"
                        value={platformSettings.max_templates_per_org || 100}
                        onChange={(e) => setPlatformSettings({...platformSettings, max_templates_per_org: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2 pt-4 border-t">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Label>Maintenance Mode</Label>
                        <p className="text-xs text-muted-foreground">Block all users except master admin</p>
                      </div>
                      <Switch
                        checked={platformSettings.maintenance_mode || false}
                        onCheckedChange={(checked) => setPlatformSettings({...platformSettings, maintenance_mode: checked})}
                      />
                    </div>
                    {platformSettings.maintenance_mode && (
                      <Textarea
                        placeholder="Maintenance message to show users..."
                        value={platformSettings.maintenance_message || ''}
                        onChange={(e) => setPlatformSettings({...platformSettings, maintenance_message: e.target.value})}
                        className="mb-4"
                      />
                    )}
                    <Button onClick={handleSaveSettings} className="btn-primary">
                      Save Settings
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Announcements */}
            <div className="neumorphic rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-lg">Announcements</h3>
                <Button size="sm" onClick={() => setShowAnnouncementModal(true)}>
                  <Megaphone className="w-4 h-4 mr-1" />
                  New Announcement
                </Button>
              </div>
              
              {announcements.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No announcements</p>
              ) : (
                <div className="space-y-3">
                  {announcements.map(a => (
                    <div key={a.id} className={`p-4 rounded-lg border ${
                      a.type === 'critical' ? 'bg-red-50 border-red-200' :
                      a.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                      'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">{a.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{a.message}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(a.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500"
                          onClick={() => handleDeleteAnnouncement(a.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="neumorphic rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-semibold">Activity Logs</h2>
              <Button variant="outline" size="sm" onClick={loadActivityLogs}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-2">
              {activityLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No activity logs</p>
              ) : (
                activityLogs.map((log, idx) => (
                  <div key={log.id || `${log.timestamp}-${log.action}-${idx}`} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                    <Activity className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{log.action}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {log.entity_type}: {log.entity_id}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading text-xl font-semibold mb-6">Create New Organization</h3>
            
            <form onSubmit={handleCreateOrganization} className="space-y-4">
              <div className="space-y-2">
                <Label>Organization Name</Label>
                <Input
                  value={newOrg.name}
                  onChange={(e) => setNewOrg(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Acme Recruitment Ltd"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Plan</Label>
                <select
                  value={newOrg.plan}
                  onChange={(e) => setNewOrg(prev => ({ ...prev, plan: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="create-org-plan-select"
                >
                  {/* Plan IDs match the public pricing page (/pricing) catalog.
                      Labels mirror the outcome-led tier names shown there. */}
                  <option value="forms">Starter — £149/mo · 3 recruiters · forms only</option>
                  <option value="documents">Pro — £299/mo · 10 recruiters · forms + PDFs (most popular)</option>
                  <option value="complete">Compliance — £599/mo · unlimited · adds credentials</option>
                  <option value="enterprise">Enterprise — custom · SSO, white-label, SLA</option>
                </select>
                <label className="flex items-center gap-2 pt-1 text-xs text-slate-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={newOrg.start_as_trial}
                    onChange={(e) => setNewOrg(prev => ({ ...prev, start_as_trial: e.target.checked }))}
                    data-testid="create-org-trial-checkbox"
                  />
                  Start as 14-day free trial
                </label>
              </div>
              
              <hr className="border-border" />
              <p className="text-sm text-muted-foreground">Admin User Details</p>
              
              <div className="space-y-2">
                <Label>Admin Name</Label>
                <Input
                  value={newOrg.admin_name}
                  onChange={(e) => setNewOrg(prev => ({ ...prev, admin_name: e.target.value }))}
                  placeholder="John Doe"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Admin Email</Label>
                <Input
                  type="email"
                  value={newOrg.admin_email}
                  onChange={(e) => setNewOrg(prev => ({ ...prev, admin_email: e.target.value }))}
                  placeholder="admin@acme.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Admin Password</Label>
                <Input
                  type="password"
                  value={newOrg.admin_password}
                  onChange={(e) => setNewOrg(prev => ({ ...prev, admin_password: e.target.value }))}
                  placeholder="Secure password"
                  required
                  minLength={6}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1" disabled={creating}>
                  {creating ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Creating...</> : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="font-heading text-xl font-semibold mb-6">
              Add User to {selectedOrg.organization.name}
            </h3>
            
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))} placeholder="John Smith" required />
              </div>
              <div className="space-y-2">
                <Label>Email (used as login username)</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))} placeholder="john@company.com" required />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input value={newUser.password} onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))} placeholder="Min 6 characters" required minLength={6} />
                <p className="text-xs text-muted-foreground">This password will be visible in the admin panel</p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <select value={newUser.role} onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddUserModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="btn-primary flex-1">Add User</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bullhorn Config Modal */}
      {showBullhornModal && selectedOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl" data-testid="bullhorn-config-modal">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#0066B3] flex items-center justify-center">
                <Server className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold">Bullhorn API Configuration</h3>
                <p className="text-sm text-muted-foreground">{selectedOrg.organization.name}</p>
              </div>
            </div>
            
            <form onSubmit={handleUpdateBullhorn} className="space-y-4 mt-4">
              {/* Region Selector */}
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Bullhorn Region</Label>
                <div className="flex gap-2">
                  {[
                    { label: 'US / Default', authUrl: 'https://auth.bullhornstaffing.com/oauth/authorize', restUrl: 'https://rest.bullhornstaffing.com/rest-services/' },
                    { label: 'EMEA', authUrl: 'https://auth-emea.bullhornstaffing.com/oauth/authorize', restUrl: 'https://rest-emea.bullhornstaffing.com/rest-services/' },
                    { label: 'APAC', authUrl: 'https://auth-apac.bullhornstaffing.com/oauth/authorize', restUrl: 'https://rest-apac.bullhornstaffing.com/rest-services/' },
                  ].map(r => (
                    <Button
                      key={r.label}
                      type="button"
                      variant={bullhornConfig.auth_url === r.authUrl ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBullhornConfig(prev => ({ ...prev, auth_url: r.authUrl, rest_url: r.restUrl }))}
                    >
                      {r.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <Input 
                    value={bullhornConfig.client_id} 
                    onChange={(e) => setBullhornConfig(prev => ({ ...prev, client_id: e.target.value }))} 
                    placeholder="xxxxxxxx-xxxx-xxxx"
                    className="font-mono text-sm"
                    required 
                    data-testid="bh-client-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Secret</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.secret ? 'text' : 'password'}
                      value={bullhornConfig.client_secret}
                      onChange={(e) => setBullhornConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                      placeholder="Your client secret"
                      className="font-mono text-sm pr-10"
                      required
                      data-testid="bh-client-secret"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPasswords(p => ({ ...p, secret: !p.secret }))}>
                      {showPasswords.secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>API Username</Label>
                  <Input 
                    value={bullhornConfig.username} 
                    onChange={(e) => setBullhornConfig(prev => ({ ...prev, username: e.target.value }))} 
                    placeholder="company.api.user"
                    className="font-mono text-sm"
                    required 
                    data-testid="bh-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label>API Password</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.password ? 'text' : 'password'}
                      value={bullhornConfig.password}
                      onChange={(e) => setBullhornConfig(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Your API password"
                      className="font-mono text-sm pr-10"
                      required
                      data-testid="bh-password"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPasswords(p => ({ ...p, password: !p.password }))}>
                      {showPasswords.password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowBullhornModal(false); setBullhornConfig({ client_id: '', client_secret: '', username: '', password: '', auth_url: 'https://auth.bullhornstaffing.com/oauth/authorize', rest_url: 'https://rest.bullhornstaffing.com/rest-services/' }); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary flex-1" data-testid="save-bullhorn-config-btn">
                  Save Credentials
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="font-heading text-xl font-semibold mb-6">New Announcement</h3>
            
            <form onSubmit={handleCreateAnnouncement} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea value={newAnnouncement.message} onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))} required rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select value={newAnnouncement.type} onChange={(e) => setNewAnnouncement(prev => ({ ...prev, type: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAnnouncementModal(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="btn-primary flex-1">Create</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="font-heading text-xl font-semibold mb-6">Edit User</h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editingUser.name} onChange={(e) => setEditingUser(prev => ({ ...prev, name: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label>Email (login username)</Label>
                <Input type="email" value={editingUser.email} onChange={(e) => setEditingUser(prev => ({ ...prev, email: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <select value={editingUser.role} onChange={(e) => setEditingUser(prev => ({ ...prev, role: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditingUser(null)} className="flex-1">Cancel</Button>
                <Button type="submit" className="btn-primary flex-1">Save Changes</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
